import { useFlickity } from '@/components/hooks/useFlickity'
import { BaseResults } from 'libs/athos/components/recommendations/BaseResults'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { useState, useEffect } from 'preact/hooks'
import clsx from 'clsx'
import { shouldHideCarouselArrows } from '@/components/helpers/carousel'

export function CarouselResults({
  className,
  wrapperClassName = '',
  options = { pageDots: true, cellAlign: 'left' },
  productCardSettings,
  beaconEnabled = true,
  'data-dl-section-id': dataDlSectionId
}) {
  const { results, isLoading } = useAthosContext()
  const [realNode, setRealNode] = useState()
  const [containerNode, setContainerNode] = useState()
  const [showRealCarousel, setShowRealCarousel] = useState(false)
  const [hideArrows, setHideArrows] = useState(false)

  // The skeleton is a static placeholder, not a carousel — deliberately no
  // `useFlickity` call for it, or every recommendations carousel would promote a
  // second slider layer before its data arrives.
  const { SliderPortal, flickity, inViewRange } = useFlickity(realNode, options, [results])

  const hasData = !isLoading && results?.results

  useEffect(() => {
    const cellCount = results?.results?.length ?? 0
    const updateArrowsVisibility = () => setHideArrows(shouldHideCarouselArrows(cellCount, options.columns, options.columnsMb))
    updateArrowsVisibility()
    window.addEventListener('resize', updateArrowsVisibility)
    return () => window.removeEventListener('resize', updateArrowsVisibility)
  }, [results, options.columns, options.columnsMb])

  useEffect(() => {
    if (!hasData || !containerNode) {
      setShowRealCarousel(false)
      return
    }

    if (!flickity) {
      // Flickity only exists near the viewport, but content must not wait for it:
      // show the real cards immediately and let Flickity enhance in place on approach.
      if (!inViewRange) setShowRealCarousel(true)
      return
    }

    const skeletonHeight = containerNode.getBoundingClientRect().height
    containerNode.style.height = `${skeletonHeight}px`

    flickity.resize()
    flickity.reloadCells()

    const timer = setTimeout(() => {
      containerNode.style.height = ''
      setShowRealCarousel(true)
    }, 100)

    return () => clearTimeout(timer)
  }, [hasData, flickity, containerNode, inViewRange])

  return (
    <BaseResults
      className={className}
      wrapperClassName={wrapperClassName}
      productCardSettings={productCardSettings}
      beaconEnabled={beaconEnabled}
      insideCarousel={true}
      renderWrapper={(skeletonItems, realItems) => (
        <div ref={setContainerNode} className="relative w-full grid grid-cols-1 grid-rows-1 transition-all">
          <div className={clsx(
            'w-full row-start-1 col-start-1 transition-opacity',
            !showRealCarousel ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}>
            <div
              className={clsx(
                'flex w-full',
                options.pageDots && 'pb-1.5 lg:pb-1.625'
              )}
            >
              {skeletonItems}
            </div>
          </div>

          <div className={clsx(
            'w-full row-start-1 col-start-1 transition-opacity',
            showRealCarousel ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}>
            <div
              ref={setRealNode}
              className={clsx(
                'flex w-full',
                options.pageDots && 'pb-1.5 lg:pb-1.625',
                hideArrows && 'carousel-arrows-hidden'
              )}
              data-dl-section-id={dataDlSectionId}
            >
              <SliderPortal>
                {realItems}
              </SliderPortal>
            </div>
          </div>
        </div>
      )}
    />
  )
}
